import { readSizingSession, rememberSizingSession, type SizingSelection } from '../pose/SizingSession';
export type { SizingSelection } from '../pose/SizingSession';
import type { FrameSpec } from '../config/frames';
import { HANDMADE_SIZES, handmadeScaleForWidth, PD_MAX_MM, PD_MIN_MM } from '../pose/Sizing';
import { btn, h } from './icons';
import { words } from './words';

const FACE_PD = [55, 60, 65] as const;

/**
 * A42E « Taille » view: head size (sets the estimated PD), known PD, and the
 * Handmade frame-width ruler (XS–XL preview, not a product variant).
 * Session-only: the selection survives a model change, never a reload.
 */
export class SizingControls {
  readonly root = h('div', 'mt-view mt-size-view');
  private readonly status = h('span', 'mt-pd-status');
  private readonly faceButtons: HTMLButtonElement[] = [];
  private pd = 60;
  private manualPd = false;
  private selectedFace = 60;
  private width = 145;
  private readonly handmade: boolean;
  selection(): SizingSelection { return { pd: this.pd, manual: this.manualPd, face: this.selectedFace, width: this.width }; }

  constructor(spec: FrameSpec, private readonly onPd: (mm: number | null) => void, onScale: (scale: number) => void, initial?: SizingSelection, private readonly onSelection?: () => void) {
    initial ??= readSizingSession();
    this.pd = initial?.pd ?? 60; this.manualPd = initial?.manual ?? false;
    this.selectedFace = initial?.face ?? 60; this.width = initial?.width ?? 145;
    this.handmade = spec.line === 'Handmade';
    const w = words();
    this.root.dataset.view = 'size';
    this.root.setAttribute('role', 'group');
    this.root.setAttribute('aria-label', w.pill[0]);

    const question = h('p', 'mt-size-q', w.headQuestion);
    const faces = h('div', 'mt-seg mt-faces');
    faces.setAttribute('role', 'group'); faces.setAttribute('aria-label', w.headQuestion);
    FACE_PD.forEach((mm, i) => {
      const b = btn('mt-seg-item mt-face-option', w.faces[i]);
      b.dataset.pd = String(mm);
      b.setAttribute('aria-label', `${w.faces[i]} · ${mm} mm`);
      b.onclick = () => { this.selectedFace = mm; this.useFace(onPd); };
      this.faceButtons.push(b); faces.append(b);
    });

    // Known PD
    const pdRow = h('div', 'mt-pd-row');
    const pdToggle = btn('mt-link mt-pd-toggle', w.pdOpen);
    pdToggle.setAttribute('aria-expanded', 'false');
    pdRow.append(this.status, pdToggle);
    const form = h('form', 'mt-pd-form'); form.hidden = true;
    form.id = `mt-pd-form-${Math.random().toString(36).slice(2, 8)}`;
    pdToggle.setAttribute('aria-controls', form.id);
    const inputWrap = h('div', 'mt-pd-input');
    const input = h('input');
    input.type = 'number'; input.inputMode = 'numeric'; input.min = String(PD_MIN_MM); input.max = String(PD_MAX_MM); input.step = '1';
    input.placeholder = '62'; input.setAttribute('aria-label', w.pdInput);
    const unit = h('span', '', 'mm'); unit.setAttribute('aria-hidden', 'true');
    inputWrap.append(input, unit);
    const apply = h('button', 'mt-btn-ink', w.apply); apply.type = 'submit';
    const reset = btn('mt-link mt-link--muted mt-pd-reset', w.useFace);
    form.append(inputWrap, apply, reset);
    const setForm = (open: boolean) => {
      form.hidden = !open;
      pdToggle.textContent = open ? w.pdClose : w.pdOpen;
      pdToggle.setAttribute('aria-expanded', String(open));
      if (open) input.focus({ preventScroll: true });
    };
    pdToggle.onclick = () => setForm(form.hidden);
    reset.onclick = () => { input.value = ''; this.useFace(onPd); };
    form.onsubmit = event => {
      event.preventDefault();
      if (input.value === '') { this.useFace(onPd); setForm(false); return; }
      if (!input.reportValidity()) return;
      const v = input.valueAsNumber;
      if (!(v >= PD_MIN_MM && v <= PD_MAX_MM)) return;
      this.pd = v; this.manualPd = true;
      this.syncFaces();
      this.changed();
      onPd(this.pd); this.updateCalibration(false);
      setForm(false);
    };

    const adjustment = h('details', 'mt-pd-adjustment');
    const summary = h('summary'); summary.append(this.status);
    adjustment.append(summary, question, faces, pdRow, form);
    this.root.append(adjustment);

    if (this.handmade) {
      const block = h('div', 'mt-frame-size');
      const label = h('span', 'mt-frame-size-label');
      label.append(h('span', 'mt-long', w.frameSize), h('span', 'mt-short', w.frameSizeShort));
      label.id = `mt-frame-size-${Math.random().toString(36).slice(2, 8)}`;
      const ruler = h('div', 'mt-ruler');
      const ticksRow = h('div', 'mt-ruler-ticks');
      const ticks: HTMLElement[] = [];
      for (let i = 0; i <= 20; i++) {
        const tick = h('span', `mt-tick${i % 5 === 0 ? ' mt-tick--major' : ''}`);
        ticks.push(tick); ticksRow.append(tick);
      }
      const hit = h('div', 'mt-ruler-hit');
      const labels = h('div', 'mt-ruler-labels');
      labels.setAttribute('role', 'group'); labels.setAttribute('aria-labelledby', label.id);
      const buttons: HTMLButtonElement[] = [];
      const select = (widthMm: number) => {
        this.width = widthMm;
        const index = HANDMADE_SIZES.findIndex(s => s.widthMm === widthMm);
        ticks.forEach((t, i) => t.classList.toggle('mt-tick--on', i === index * 5));
        buttons.forEach((b, i) => b.setAttribute('aria-pressed', String(i === index)));
      };
      for (const size of HANDMADE_SIZES) {
        const choose = () => {
          select(size.widthMm);
          rememberSizingSession(this.selection());
          this.changed();
          onScale(handmadeScaleForWidth(spec, size.widthMm));
        };
        const area = btn('mt-ruler-hit-area'); area.tabIndex = -1; area.setAttribute('aria-hidden', 'true'); area.onclick = choose;
        hit.append(area);
        const b = btn('mt-size-option', size.label);
        b.setAttribute('aria-label', `${size.label} · ${size.widthMm} mm`);
        b.onclick = choose;
        buttons.push(b); labels.append(b);
      }
      ticksRow.append(hit);
      ruler.append(ticksRow, labels);
      block.append(label, ruler);
      this.root.prepend(block);
      select(this.width);
      onScale(handmadeScaleForWidth(spec, this.width));
    }

    if (this.manualPd) {
      input.value = String(this.pd);
      this.syncFaces();
      rememberSizingSession(this.selection());
      onPd(this.pd); this.updateCalibration(false);
    } else this.useFace(onPd, false);
  }

  /** « Gardé : visage moyen · taille M » — what survives a model change. */
  keptLabel(): string {
    const w = words();
    const face = this.manualPd ? null : w.facesKept[FACE_PD.indexOf(this.selectedFace as typeof FACE_PD[number])] ?? null;
    const size = this.handmade ? HANDMADE_SIZES.find(s => s.widthMm === this.width)?.label ?? null : null;
    return w.kept(face, this.pd, size);
  }

  updateCalibration(ready: boolean): void {
    const w = words();
    const text = !this.manualPd ? w.pdEstimated(this.pd) : ready ? w.pdKnown(this.pd) : `${w.pdKnown(this.pd)} — ${w.pdCalibrating}`;
    if (this.status.textContent !== text) this.status.textContent = text;
  }

  setCalibration(pd: number, manual: boolean, face = this.selectedFace): void {
    if (!Number.isFinite(pd) || pd < PD_MIN_MM || pd > PD_MAX_MM) return;
    this.pd = pd; this.manualPd = manual; this.selectedFace = face;
    this.syncFaces(); rememberSizingSession(this.selection());
    this.onPd(pd); this.updateCalibration(false); this.changed();
  }

  private useFace(onPd: (mm: number | null) => void, notify = true): void {
    this.manualPd = false; this.pd = this.selectedFace;
    this.syncFaces();
    rememberSizingSession(this.selection());
    if (notify) this.changed();
    onPd(this.pd); this.updateCalibration(false);
  }

  private syncFaces(): void {
    this.faceButtons.forEach(b => b.setAttribute('aria-pressed', String(!this.manualPd && Number(b.dataset.pd) === this.selectedFace)));
  }

  private changed(): void { rememberSizingSession(this.selection()); this.onSelection?.(); }
}
